import {type} from "@colyseus/schema";
import {nanoid} from "nanoid";
import {GameSchema} from "./game_element_schema";
import {Ingredient, IngredientTypes} from "./ingredients";

export enum StationType {
    CUTTING_BOARD = "cutting_board",
    STOVE = "stove",
    COUNTER = "counter",
    TRASH = "trash",
}

// Station recipes: what each station type transforms.
// Must remain consistent with RECIPES in recipe_registry.ts.
const STATION_RECIPES: Map<StationType, Map<IngredientTypes, IngredientTypes>> = new Map<StationType, Map<IngredientTypes, IngredientTypes>>([
    [StationType.CUTTING_BOARD, new Map([
        [IngredientTypes.TOMATO, IngredientTypes.CHOPPED_TOMATO],
        [IngredientTypes.SALAD, IngredientTypes.CHOPPED_SALAD],
        [IngredientTypes.ONION, IngredientTypes.CHOPPED_ONION],
    ])],
    [StationType.STOVE, new Map([
        [IngredientTypes.CHOPPED_TOMATO, IngredientTypes.TOMATO_SOUP],
        [IngredientTypes.CHOPPED_ONION, IngredientTypes.ONION_SOUP],
        [IngredientTypes.VEGGIE_MIX, IngredientTypes.VEGGIE_STEW],
    ])],
]);

export function stationOutputType(stationType: StationType, inputType: IngredientTypes): IngredientTypes | undefined {
    return STATION_RECIPES.get(stationType)?.get(inputType);
}

/** Returns true if the given ingredient type is an output of the specified station type. */
export function isStationOutput(stationType: StationType, ingredientType: IngredientTypes): boolean {
    return [...(STATION_RECIPES.get(stationType)?.values() ?? [])].includes(ingredientType);
}

export class Station extends GameSchema {
    @type("string") stationType: StationType
    @type("string") ingredientId: string | undefined
    @type("number") progress: number = 0       // 0 to 1
    @type("number") burnProgress: number = 0   // 0 to 1 (after cooking completes)
    @type("boolean") isProcessing: boolean = false
    @type("string") activeChopperId: string | undefined

    // server-only (not synced via Colyseus schema)
    ingredient: Ingredient

    constructor(x: number, y: number, stationType: StationType) {
        super();
        this.id = nanoid(8);
        this.x = x;
        this.y = y;
        this.stationType = stationType;
    }
}
